import React, { useEffect, useState } from 'react';
import { Container, Table, TableHead, TableBody, TableCell, TableRow, TextField, Grid, Button, Pagination, CircularProgress, Alert, Snackbar, Typography, Paper, Box, Card, CardContent } from '@mui/material';

function InventoryShopify() {
  const [shopifyInventory, setShopifyInventory] = useState([]);
  const [filtroTexto, setFiltroTexto] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const rowsPerPage = 100;

  // Configuración de Shopify
  const SHOPIFY_STORE = 'dev-parma-store.myshopify.com';
  const ACCESS_TOKEN = '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';

  // Función para obtener datos de Shopify
  const fetchShopifyData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const PROXY_URL = 'https://corsproxy.io/?';
      const SHOPIFY_URL = `https://${SHOPIFY_STORE}/admin/api/2024-01/graphql.json`;
      
      let allItems = [];
      let hasNextPage = true;
      let afterCursor = null;
  
      while (hasNextPage) {
        const response = await fetch(PROXY_URL + encodeURIComponent(SHOPIFY_URL), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Access-Token': ACCESS_TOKEN,
          },
          body: JSON.stringify({
            query: `query($first: Int, $after: String) {
              inventoryItems(first: $first, after: $after) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    id
                    sku
                    tracked
                  }
                }
              }
            }`,
            variables: { first: 250, after: afterCursor }
          })
        });
  
        const data = await response.json();
        
        if (data.errors) throw new Error(data.errors[0].message);
        
        allItems = [...allItems, ...data.data.inventoryItems.edges.map(edge => edge.node)];
        hasNextPage = data.data.inventoryItems.pageInfo.hasNextPage;
        afterCursor = data.data.inventoryItems.pageInfo.endCursor;
      }
  
      setShopifyInventory(allItems);
      setSuccess(`Obtenidos ${allItems.length} artículos`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  // Filtrar datos según el texto de búsqueda
  const filteredData = shopifyInventory.filter(item => 
    item.sku && item.sku.toLowerCase().includes(filtroTexto.toLowerCase())
  );

  // Calcular datos para la página actual
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Cambiar página
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    fetchShopifyData();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          INVENTARIO - PARMA STORE
        </Typography>
        <Box sx={{ borderBottom: 2, borderColor: 'divider', mb: 4 }} />

        {/* Alertas */}
        {error && (
          <Snackbar open={true} autoHideDuration={6000} onClose={() => setError(null)}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Snackbar>
        )}
        
        {success && (
          <Snackbar open={true} autoHideDuration={6000} onClose={() => setSuccess(null)}>
            <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
          </Snackbar>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Buscar por SKU"
              variant="outlined"
              fullWidth
              value={filtroTexto}
              onChange={(e) => {
                setFiltroTexto(e.target.value);
                setCurrentPage(1); // Resetear a la primera página al buscar
              }}
            />
          </Grid>

          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={fetchShopifyData}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Actualizar Datos
            </Button>
          </Grid>
        </Grid>

        {/* Información de la tienda */}
        <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
          <CardContent>
            <Typography variant="body2">
              <strong>Tienda Shopify:</strong> {SHOPIFY_STORE}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Mostrando {filteredData.length} items de inventario
            </Typography>
          </CardContent>
        </Card>

        {/* Tabla de inventario */}
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 650 }} aria-label="tabla de inventario">
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>SKU</strong></TableCell>
                  <TableCell><strong>Seguimiento</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.id.split('/').pop()}</TableCell>
                      <TableCell>{item.sku || 'N/A'}</TableCell>
                      <TableCell>
                        {item.tracked ? (
                          <Typography color="success.main">Activado</Typography>
                        ) : (
                          <Typography color="error">Desactivado</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No se encontraron items de inventario
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Paginación */}
            {filteredData.length > rowsPerPage && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default InventoryShopify;